import { InputButton, type PlayerInput } from "@arcade-hockey/shared";
import type { MatchState, PlayerState } from "@arcade-hockey/sim";
import { enemyNetX } from "../geometry/net.js";
import { bestPassTarget, safestOutletTarget } from "../geometry/pass-lane.js";
import { nearestOpponentTo } from "../geometry/threat.js";

// Shoot-range: anywhere inside the offensive zone-ish. Generous so bots
// take shots from the slot rather than skating right into the goalie.
const SHOT_RANGE = 15;
// Anything closer than this is considered "pressured" — the carrier prefers
// to dump the puck rather than try to deke.
const PRESSURE_RADIUS = 2.8;
// Cornered: a tighter threshold than pressure. When a defender is on us
// AND we have no pass outlet, this is when the dump-in fires — the
// carrier is about to be stripped, so a controlled chuck deep beats a
// silent fumble.
const CORNERED_RADIUS = 1.5;
// Max distance to enemy net at which the dump-in is allowed. Real hockey
// dump-ins happen at the offensive blue line and inward — chucking the
// puck deep into the OZ. Outside this range a "dump-in" would actually
// be a backwards or NZ giveaway. Critically, this stops the centre from
// firing the puck at the enemy net immediately after the faceoff just
// because the other centre is body-on-body.
const DUMP_RANGE = 22;
// Burst speed when there's daylight — anyone outside this radius isn't an
// immediate threat, so the carrier sprints into open ice rather than
// matching the rest of the league at base speed. Real hockey carriers
// accelerate into space; without this the defender sprint-trigger (~2m)
// always closes from behind because the carrier moves at base speed.
const OPEN_ICE_SPRINT_RADIUS = 5;
// Tick count to hold Shoot before releasing. Above QUICK_THRESHOLD_TICKS=6
// in shoot.ts so the release is a "charged" shot (between quickSpeed=16 and
// chargedSpeed=22 m/s). Slow enough that nearby defenders sometimes get a
// chance to interrupt.
const SHOT_CHARGE_TICKS = 12;
// Pass charge: a short hold to gain some momentum on the puck (pass speeds
// scale linearly from speedMin=4 to speedMax=7.5 m/s over holdMaxTicks=30).
const PASS_CHARGE_TICKS = 4;

// Carrier behavior. Picks one of three actions each tick:
//   PASS    — pressured AND a teammate has a clear up-ice lane
//   SHOOT   — in shot range, OR pressured-and-stuck (dump-in fallback)
//   SKATE   — otherwise: head for the net, sprint when in open ice
//
// stickDir follows moveDir in SimTick.ts when moveMag > 0.2, so whatever
// direction we command for movement also sets the aim. That's intentional —
// we want the carrier moving toward what it's about to shoot at or pass to.
export function fillCarrierInput(input: PlayerInput, player: PlayerState, state: MatchState): void {
  const netX = enemyNetX(player.teamId);
  const netY = 0;
  const dxToNet = netX - player.pos.x;
  const dyToNet = netY - player.pos.y;
  const distToNet = Math.hypot(dxToNet, dyToNet);

  const nearest = nearestOpponentTo(player, state);
  const pressureDist = nearest === null ? Number.POSITIVE_INFINITY : nearest.distance;
  const pressured = pressureDist < PRESSURE_RADIUS;

  // 1. Pressured: try to move the puck before getting stripped. First
  // look for a meaningful up-ice option (see `bestPassTarget`). If
  // nothing forward qualifies AND we're cornered (~1.5 m of an
  // opponent), fall back to a safest-outlet pass — a back-/side-pass
  // to a teammate in open ice. This is the real-hockey "send it back
  // to the D" outlet the faceoff-winning centre uses to start a
  // controlled breakout instead of dueling forever with the opposing
  // centre.
  if (pressured) {
    let target = bestPassTarget(player, state);
    if (target === null && pressureDist < CORNERED_RADIUS) {
      target = safestOutletTarget(player, state);
    }
    if (target !== null) {
      aimAt(input, player, target.pos.x, target.pos.y);
      if (player.passHoldTicks >= PASS_CHARGE_TICKS) {
        input.buttons = 0; // release → pass fires
      } else {
        // Shield the puck while charging the pass. Context (protect)
        // makes stick-lifts fail and reduces our top speed — the right
        // move when committed to a pass rather than the rush.
        input.buttons = InputButton.Pass | InputButton.Context;
      }
      return;
    }
    // No pass outlet under pressure — fall through to dump-in / shot.
  }

  // 2. In shot range, OR cornered-with-no-outlet WHILE inside dump
  // range: fire the puck. The dump-in only fires under tight pressure
  // (CORNERED_RADIUS = 1.5 m) AND with the enemy net within DUMP_RANGE
  // — that's the real-hockey "I'm about to be stripped at the OZ blue
  // line" moment. Without the dump-range gate the centre would dump the
  // puck right after the faceoff because the other centre is body-on-body.
  const cornered = pressureDist < CORNERED_RADIUS;
  if (distToNet < SHOT_RANGE || (cornered && distToNet < DUMP_RANGE)) {
    aimAt(input, player, netX, netY);
    if (player.shootHoldTicks >= SHOT_CHARGE_TICKS) {
      input.buttons = 0; // release → shot fires
    } else {
      input.buttons = InputButton.Shoot;
    }
    return;
  }

  // 3. Otherwise: head for the net, sprinting when there's daylight.
  // Without a sprint trigger here the carrier coasts at base speed
  // while defenders sprint — every engagement caught up from behind.
  // Now the carrier accelerates into open ice and forces defenders to
  // pick their angle.
  aimAt(input, player, netX, netY);
  if (pressureDist > OPEN_ICE_SPRINT_RADIUS) {
    input.sprint = 1;
  }
}

function aimAt(input: PlayerInput, player: PlayerState, targetX: number, targetY: number): void {
  const dx = targetX - player.pos.x;
  const dy = targetY - player.pos.y;
  const mag = Math.hypot(dx, dy);
  if (mag > 0.0001) {
    input.moveX = dx / mag;
    input.moveY = dy / mag;
  } else {
    input.moveX = 0;
    input.moveY = 0;
  }
}
